const S3_MEDIA_HOST = 'https://scw-commerce-media.s3.amazonaws.com';
const STOREFRONT_HOSTS = new Set(['getscw.com', 'www.getscw.com']);
const MEDIA_PATH_PATTERN = /^\/(?:images|pub\/media|media|downloads)(?:\/|$)/;
const PUB_MEDIA_PREFIX = /^\/pub\/media(?=\/|$)/;
const SVG_PATH_PATTERN = /\.svg$/i;
const VIDEO_PATH_PATTERN = /\.(?:mp4|m4v|mov|webm)$/i;

/**
 * Splits a media URL into its bucket-relative path (`/images/...`,
 * `/pub/media/...`, ...) and its query/hash suffix. Returns null for anything
 * that is not media on the storefront host, the S3 bucket, or a root-relative
 * media path.
 */
function parseMediaSource(source: string): { path: string; suffix: string } | null {
  if (source.startsWith('/')) {
    if (source.startsWith('//')) return null;
    const match = /^([^?#]*)([\s\S]*)$/u.exec(source);
    const path = match?.[1] ?? source;
    return MEDIA_PATH_PATTERN.test(path) ? { path, suffix: match?.[2] ?? '' } : null;
  }

  let url: URL;
  try {
    url = new URL(source);
  } catch {
    return null;
  }
  if (url.origin !== S3_MEDIA_HOST && !STOREFRONT_HOSTS.has(url.hostname)) return null;
  if (!MEDIA_PATH_PATTERN.test(url.pathname)) return null;
  return { path: url.pathname, suffix: `${url.search}${url.hash}` };
}

/**
 * Root-relative storefront URL for a media asset, for anything the browser
 * loads straight from the HTML: raw `<img>` src/srcset, `<video poster>`,
 * CSS `url()`, links to image or PDF files.
 *
 * Images in page HTML stay on the storefront host so search engines credit
 * them to getscw.com rather than the bucket. `pub/media/...` keys come back
 * as `/media/...` (src/app/media/[...path] tries `pub/media/` then `media/`
 * and sets the right Content-Type); `/images/...` and `/downloads/...` keep
 * their path. Videos keep their URL (playback depends on byte ranges, which
 * the bucket serves directly). Anything else is returned unchanged.
 */
export function toStorefrontMediaUrl(source: string): string {
  const media = parseMediaSource(source);
  if (!media || VIDEO_PATH_PATTERN.test(media.path)) return source;
  return `${media.path.replace(PUB_MEDIA_PREFIX, '/media')}${media.suffix}`;
}

/**
 * `src` for a `next/image` that the optimizer resizes. The optimizer fetches
 * remote sources over HTTP, and a storefront URL would loop back through the
 * CDN to this app, so `/images`, `/pub/media` and `/downloads` sources point
 * at the bucket. The rendered `<img>` only carries `/_next/image?url=...`, so
 * the bucket host never reaches the page as a link.
 *
 * Two cases stay on the storefront path instead: `/media/...`, whose bucket
 * key can be under `pub/media/` or `media/` (only the route knows, and the
 * optimizer reaches it in-process), and SVGs, which next/image never
 * optimizes and renders as a raw `src`.
 */
export function getImageOptimizerSrc(source: string): string {
  const media = parseMediaSource(source);
  if (!media) return source;
  if (SVG_PATH_PATTERN.test(media.path)) return toStorefrontMediaUrl(source);
  if (media.path === '/media' || media.path.startsWith('/media/')) return `${media.path}${media.suffix}`;
  return `${S3_MEDIA_HOST}${media.path}${media.suffix}`;
}

/** `srcset` value with each candidate URL served from the storefront. */
export function routeSrcset(srcset: string): string {
  return srcset
    .split(',')
    .map((candidate) =>
      candidate.replace(
        /^(\s*)(\S+)([\s\S]*)$/u,
        (_candidate, leading: string, source: string, descriptor: string) =>
          `${leading}${toStorefrontMediaUrl(source)}${descriptor}`,
      ),
    )
    .join(',');
}

function routeCssUrls(css: string): string {
  return css.replace(
    /url\((["']?)([^"')]+)\1\)/gi,
    (_match, quote: string, source: string) =>
      `url(${quote}${toStorefrontMediaUrl(source.trim())}${quote})`,
  );
}

/**
 * Points browser-facing media attributes in stored HTML (src, href, poster,
 * srcset, CSS url()) at the storefront host. Source-faithful `data-*`
 * attributes, which the page-rebuild parity gate reads, are left alone.
 */
export function routeStorefrontMediaInHtml(html: string): string {
  const withUrlAttributes = html.replace(
    /(?<![\w-])(src|href|poster)=(["'])([^"']*)\2/gi,
    (_match, attribute: string, quote: string, source: string) =>
      `${attribute}=${quote}${toStorefrontMediaUrl(source)}${quote}`,
  );

  const withSrcsets = withUrlAttributes.replace(
    /(?<![\w-])(srcset)=(["'])([^"']*)\2/gi,
    (_match, attribute: string, quote: string, value: string) =>
      `${attribute}=${quote}${routeSrcset(value)}${quote}`,
  );

  return routeCssUrls(withSrcsets);
}
